import { useState, useEffect } from 'react';
import { getTrendsAnalytics } from '../../services/trendsService';
import LineChart from '../charts/LineChart';
import BarChart from '../charts/BarChart';

const TrendsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [trendsData, setTrendsData] = useState<any>(null);
  const [timeframe, setTimeframe] = useState<'daily' | 'weekly' | 'monthly'>('monthly');

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getTrendsAnalytics(90);
        setTrendsData(data);
      } catch (error) {
        console.error('Error loading trends data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading trends analytics...</p>
      </div>
    );
  }

  if (!trendsData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No trends data available</p>
      </div>
    );
  }

  // Active users chart
  const activeUsersData = {
    labels: trendsData.activeUsers[timeframe].map((d: any) => d.date),
    datasets: [
      {
        label: `Active Users (${timeframe})`,
        data: trendsData.activeUsers[timeframe].map((d: any) => d.value),
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        fill: true,
      },
    ],
  };

  // Revenue trends
  const revenueTrendsData = {
    labels: trendsData.revenue.map((d: any) => d.date),
    datasets: [
      {
        label: 'Revenue',
        data: trendsData.revenue.map((d: any) => d.value),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
      },
    ],
  };

  // Subscription trends
  const subscriptionTrendsData = {
    labels: trendsData.subscriptions.map((d: any) => d.date),
    datasets: [
      {
        label: 'New Subscriptions',
        data: trendsData.subscriptions.map((d: any) => d.value),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        fill: true,
      },
    ],
  };

  // Engagement trends
  const engagementTrendsData = {
    labels: trendsData.engagement.slice(-30).map((d: any) => d.date),
    datasets: [
      {
        label: 'Events per Day',
        data: trendsData.engagement.slice(-30).map((d: any) => d.value),
        borderColor: '#8b5cf6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        fill: true,
      },
    ],
  };

  // Growth rates
  const growthRatesData = {
    labels: ['MoM Growth', 'YoY Growth', 'Compound Growth'],
    datasets: [
      {
        label: 'Growth Rate (%)',
        data: [
          trendsData.growthRates.mom,
          trendsData.growthRates.yoy,
          trendsData.growthRates.compound,
        ],
        backgroundColor: ['#2563eb', '#10b981', '#f59e0b'],
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 'bold' }}>
          Time-Series & Trends
        </h1>
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value as 'daily' | 'weekly' | 'monthly')}
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      {/* Growth Rates KPI */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>MoM Growth</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {trendsData.growthRates.mom.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>YoY Growth</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {trendsData.growthRates.yoy.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Compound Growth</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {trendsData.growthRates.compound.toFixed(1)}%
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Active Users Trend</h3>
          <LineChart data={activeUsersData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Revenue Trend</h3>
          <LineChart data={revenueTrendsData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Subscription Trend</h3>
          <LineChart data={subscriptionTrendsData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Engagement Trend</h3>
          <LineChart data={engagementTrendsData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Growth Rates Comparison</h3>
          <BarChart data={growthRatesData} height={300} />
        </div>
      </div>
    </div>
  );
};

export default TrendsTab;

